import { useState, useEffect } from 'react';
import { CortexDialog, CortexDialogBody } from '../dialogs/CortexDialog';
import { Eye as ViewIcon, Trash2 as DeleteIcon } from 'lucide-react';
import { Button, Chip, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@coordio/ui';
import type { ChipColor } from '@coordio/ui';
import { assistantsApiService } from '../../services/assistantsApiService';
import { sessionService } from '../../services/sessionService';
import DataTable, { type DataTableColumn } from '../data-display/DataTable';
import { SessionStatus, MessageRole } from '../../types/session';
import type { AgentSessionDto, AgentMessageDto } from '../../types/session';

const statusLabels: Record<number, string> = {
  [SessionStatus.Active]: 'Active',
  [SessionStatus.Paused]: 'Paused',
  [SessionStatus.Completed]: 'Completed',
  [SessionStatus.Cancelled]: 'Cancelled',
  [SessionStatus.Expired]: 'Expired',
  [SessionStatus.Error]: 'Error',
};

const roleLabels: Record<number, string> = {
  [MessageRole.System]: 'System',
  [MessageRole.User]: 'User',
  [MessageRole.Assistant]: 'Assistant',
  [MessageRole.Tool]: 'Tool',
};

interface Props {
  agentId: string;
  agentSlug: string;
}

export const ThreadDetail = ({ agentId, agentSlug }: Props) => {
  const [threads, setThreads] = useState<AgentSessionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedThread, setSelectedThread] = useState<AgentSessionDto | null>(null);
  const [messages, setMessages] = useState<AgentMessageDto[]>([]);
  const [msgLoading, setMsgLoading] = useState(false);

  useEffect(() => {
    loadThreads();
  }, [agentId]);

  const loadThreads = async () => {
    setLoading(true);
    try {
      const result = await sessionService.getSessions({
        agentDefinitionId: agentId,
        pageSize: 100,
        searchTerm: 'Thread',
      });
      setSessions(result.items ?? []);
    } catch {
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const setSessions = (items: AgentSessionDto[]) => {
    setThreads(items);
  };

  const viewMessages = async (thread: AgentSessionDto) => {
    setSelectedThread(thread);
    setMsgLoading(true);
    try {
      const msgs = await sessionService.getMessages(thread.id);
      setMessages(msgs);
    } catch {
      setMessages([]);
    } finally {
      setMsgLoading(false);
    }
  };

  const handleCreateThread = async () => {
    try {
      await assistantsApiService.createThread(agentSlug);
      loadThreads();
    } catch (err) {
      console.error('Failed to create thread', err);
    }
  };

  const handleDeleteThread = async (threadId: string) => {
    try {
      await sessionService.deleteSession(threadId);
      loadThreads();
    } catch (err) {
      console.error('Failed to delete thread', err);
    }
  };

  const columns: DataTableColumn<AgentSessionDto>[] = [
    {
      id: 'id', label: 'Thread ID',
      render: (r) => <span className="font-mono text-xs">thread_{r.id.replace(/-/g, '')}</span>,
    },
    { id: 'sessionName', label: 'Name', render: (r) => <span className="text-sm truncate max-w-[240px] inline-block">{r.sessionName}</span> },
    { id: 'messageCount', label: 'Messages', render: (r) => r.messageCount ?? 0 },
    {
      id: 'status', label: 'Status',
      render: (r) => <Chip label={statusLabels[r.status] ?? String(r.status)} color={(r.status === SessionStatus.Active ? 'green' : 'default') as ChipColor} variant="outlined" />,
    },
    {
      id: 'createdAt', label: 'Created',
      render: (r) => new Date(r.createdAt).toLocaleString(),
    },
    {
      id: 'actions', label: '', align: 'right',
      render: (r) => (
        <div className="flex gap-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button type="button" variant="ghost" size="icon" onClick={() => viewMessages(r)}><ViewIcon size={16} /></Button>
              </TooltipTrigger>
              <TooltipContent>View messages</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button type="button" variant="ghost" size="icon" className="text-red-500" onClick={() => handleDeleteThread(r.id)}><DeleteIcon size={16} /></Button>
              </TooltipTrigger>
              <TooltipContent>Delete</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-[var(--mui-palette-text-secondary)]">
          Threads (mapped to sessions) for this agent.
        </p>
        <Button size="sm" onClick={handleCreateThread}>
          New Thread
        </Button>
      </div>

      <DataTable columns={columns} rows={threads} loading={loading} emptyMessage="No threads found." />

      <CortexDialog open={!!selectedThread} onClose={() => setSelectedThread(null)} title={<>Thread Messages — {selectedThread?.sessionName}</>} size="md" data-testid="thread-messages-dialog">
        <CortexDialogBody>
          {msgLoading ? (
            <p>Loading...</p>
          ) : messages.length === 0 ? (
            <p className="text-[var(--mui-palette-text-secondary)]">No messages in this thread.</p>
          ) : (
            <div className="flex flex-col gap-3 mt-2">
              {messages.map((m) => (
                <div
                  key={m.id}
                  className="p-4 rounded-lg border border-[var(--glass-border)]"
                  style={{
                    background: m.role === MessageRole.Assistant
                      ? 'var(--glass-bg-subtle)'
                      : m.role === MessageRole.Tool
                        ? 'rgba(255, 152, 0, 0.05)'
                        : 'transparent',
                  }}
                >
                  <span className="text-xs font-semibold uppercase text-[var(--mui-palette-primary-main)]">
                    {roleLabels[m.role] ?? m.role}
                  </span>
                  <p className="text-sm whitespace-pre-wrap mt-1">
                    {m.content}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CortexDialogBody>
      </CortexDialog>
    </div>
  );
};
